import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Alert,
  CircularProgress,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Send, CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface TestResult {
  success: boolean;
  workflowId?: string;
  status?: string;
  error?: string;
  missingFields?: string[];
  details?: any;
}

interface WorkflowSchema {
  id: string;
  name: string;
  type?: string;
  schema?: any;
}

const IroncladTest: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<TestResult | null>(null);
  const [schemas, setSchemas] = useState<WorkflowSchema[]>([]);
  const [configStatus, setConfigStatus] = useState<any>(null);
  const [selectedSchema, setSelectedSchema] = useState<WorkflowSchema | null>(null);
  const [showSchemaModal, setShowSchemaModal] = useState(false);

  const checkConfig = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/ironclad/test-ironclad/status`, {
        withCredentials: true,
      });
      setConfigStatus(response.data);
    } catch (error: any) {
      setConfigStatus({
        success: false,
        error: error.response?.data?.error || 'Failed to check configuration',
      });
    }
  };

  const runTest = async () => {
    setLoading(true);
    setResult(null);

    try {
      const response = await axios.post(`${API_BASE_URL}/api/ironclad/test-ironclad`, {}, {
        withCredentials: true,
      });
      setResult(response.data);
    } catch (error: any) {
      console.log('Error response data:', error.response?.data);
      setResult({
        success: false,
        error: error.response?.data?.error || 'Network error',
        missingFields: error.response?.data?.missingFields,
        details: error.response?.data?.details,
      });
    } finally {
      setLoading(false);
    }
  };

  const listSchemas = async () => {
    setLoading(true);
    setSchemas([]);

    try {
      const response = await axios.get(`${API_BASE_URL}/api/ironclad/list-schemas`, {
        withCredentials: true,
      });
      
      if (response.data.success) {
        setSchemas(response.data.schemas || []);
      }
      if (response.data.debug?.rawResponseSample) {
        console.log('📋 Full schemas with details available');
      }
    } catch (error: any) {
      console.error('Failed to list schemas:', error);
      console.error('Error details:', error.response?.data?.details);
      
      // Show error details to user
      setResult({
        success: false,
        error: error.response?.data?.error || 'Failed to list schemas',
        details: error.response?.data?.details,
      });
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    checkConfig();
  }, []);

  return (
    <>
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          🧪 Ironclad Sandbox Test
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Test the Ironclad integration by sending sample data to the sandbox environment.
        </Typography>
      </Box>

      {/* Configuration Status */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Configuration Status
          </Typography>
          {configStatus ? (
            configStatus.success ? (
              <Alert severity="success" icon={<CheckCircle />}>
                <Typography variant="body2">
                  <strong>Ironclad is configured and ready</strong>
                </Typography>
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  Base URL: {configStatus.baseUrl}
                </Typography>
                <Typography variant="caption" display="block">
                  Workflow Schema ID: {configStatus.workflowSchemaId}
                </Typography>
              </Alert>
            ) : (
              <Alert severity="error" icon={<ErrorIcon />}>
                <Typography variant="body2">
                  <strong>Configuration Error</strong>
                </Typography>
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  {configStatus.error}
                </Typography>
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  Make sure IRONCLAD_BASE_URL, IRONCLAD_CLIENT_ID, IRONCLAD_CLIENT_SECRET, and
                  IRONCLAD_WORKFLOW_SCHEMA_ID are set in your .env file.
                </Typography>
              </Alert>
            )
          ) : (
            <CircularProgress size={24} />
          )}
        </CardContent>
      </Card>

      {/* Test Buttons */}
      <Paper sx={{ p: 3, mb: 3, textAlign: 'center' }}>
        <Typography variant="body1" gutterBottom>
          First, list workflow schemas to find the correct template ID:
        </Typography>
        <Box sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'center' }}>
          <Button
            variant="outlined"
            color="secondary"
            size="large"
            onClick={listSchemas}
            disabled={loading || !configStatus?.success}
            startIcon={loading ? <CircularProgress size={20} /> : <CheckCircle />}
          >
            {loading ? 'Loading...' : 'List Workflow Schemas'}
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={runTest}
            disabled={loading || !configStatus?.success}
            startIcon={loading ? <CircularProgress size={20} /> : <Send />}
          >
            {loading ? 'Sending...' : 'Send Test Workflow'}
          </Button>
        </Box>
      </Paper>

      {/* Test Result */}
      {result && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Test Result
            </Typography>
            {result.success ? (
              <Alert severity="success" icon={<CheckCircle />}>
                <Typography variant="body2">
                  <strong>✅ Workflow launched successfully!</strong>
                </Typography>
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  Workflow ID: {result.workflowId}
                </Typography>
                <Typography variant="caption" display="block">
                  Status: {result.status}
                </Typography>
                <Typography variant="caption" display="block" sx={{ mt: 2 }}>
                  Check your Ironclad sandbox to see the launched workflow.
                </Typography>
              </Alert>
            ) : (
              <Alert severity="error" icon={<ErrorIcon />}>
                <Typography variant="body2">
                  <strong>❌ Test Failed</strong>
                </Typography>
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  {result.error}
                </Typography>
                {result.missingFields && result.missingFields.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" display="block">
                      <strong>Missing Required Fields:</strong>
                    </Typography>
                    <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                      {result.missingFields.map((field: string) => (
                        <li key={field}>
                          <Typography variant="caption" component="span" sx={{ fontFamily: 'monospace' }}>
                            {field}
                          </Typography>
                        </li>
                      ))}
                    </ul>
                  </Box>
                )}
                {result.details && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" display="block">
                      <strong>Error Code:</strong> {result.details.code}
                    </Typography>
                    <Typography variant="caption" display="block">
                      <strong>Message:</strong> {result.details.message}
                    </Typography>
                  </Box>
                )}
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Workflow Schemas Table */}
      {schemas.length > 0 && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Available Workflow Schemas ({schemas.length})
            </Typography>
            <TableContainer component={Paper} sx={{ mt: 2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Template ID</strong></TableCell>
                    <TableCell><strong>Name</strong></TableCell>
                    <TableCell><strong>Type</strong></TableCell>
                    <TableCell><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {schemas.map((schema) => (
                    <TableRow key={schema.id}>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                        {schema.id}
                      </TableCell>
                      <TableCell>{schema.name}</TableCell>
                      <TableCell>{schema.type || 'N/A'}</TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            setSelectedSchema(schema);
                            setShowSchemaModal(true);
                          }}
                        >
                          Show Schema
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="caption">
                Copy the Template ID for your Contractor Agreement workflow and update it in your <code>.env</code> file as <code>IRONCLAD_WORKFLOW_SCHEMA_ID</code>
              </Typography>
            </Alert>
          </CardContent>
        </Card>
      )}
    </Container>

    {/* Schema Details Modal */}
    <Dialog
      open={showSchemaModal}
      onClose={() => setShowSchemaModal(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        Schema Details: {selectedSchema?.name}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Template ID:
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ fontFamily: 'monospace', mb: 2, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}
          >
            {selectedSchema?.id}
          </Typography>
          
          <Typography variant="subtitle2" gutterBottom>
            Schema Attributes:
          </Typography>
          <pre style={{ 
            background: '#f5f5f5', 
            padding: '16px', 
            borderRadius: '4px', 
            overflow: 'auto',
            fontSize: '12px',
            maxHeight: '500px'
          }}>
            {JSON.stringify(selectedSchema?.schema || {}, null, 2)}
          </pre>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setShowSchemaModal(false)}>Close</Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default IroncladTest;
